import { apiRequest } from '@/lib/queryClient';
import API_BASE from '@/lib/apiBase';

interface Advertiser {
  id: number;
  name: string;
  address: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  campaignCount?: number;
}

interface AdvertiserInput {
  name: string;
  address?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
}

interface AdvertiserListResponse {
  success: boolean;
  data: {
    advertisers: Advertiser[];
    pagination: {
      limit: number;
      offset: number;
      total: number;
      hasMore: boolean;
    };
  };
  message: string;
}

interface AdvertiserDetailResponse {
  success: boolean;
  data: {
    advertiser: Advertiser;
  };
  message: string;
}

export async function fetchAdvertisers(
  search?: string,
  limit = 50,
  offset = 0
): Promise<Advertiser[]> {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());

  const response = await apiRequest(`/api/admin/advertisers?${params.toString()}`, 'GET');
  const jsonData = await response.json();
  
  if (!jsonData?.data?.advertisers) {
    throw new Error('Failed to fetch advertisers');
  }
  
  return jsonData.data.advertisers;
}

export async function fetchAdvertiser(id: number): Promise<Advertiser> {
  const response = await apiRequest(`/api/admin/advertisers/${id}`, 'GET');
  const jsonData = await response.json();
  
  if (!jsonData?.data?.advertiser) {
    throw new Error('Failed to fetch advertiser');
  }
  
  return jsonData.data.advertiser;
}

export async function createAdvertiser(data: AdvertiserInput): Promise<Advertiser> {
  const response = await apiRequest('/api/admin/advertisers', 'POST', data);
  const jsonData = await response.json();
  
  if (!jsonData?.data?.advertiser) {
    throw new Error(jsonData?.error || 'Failed to create advertiser');
  }
  
  return jsonData.data.advertiser;
}

export async function updateAdvertiser(
  id: number,
  data: Partial<AdvertiserInput>
): Promise<Advertiser> {
  const response = await apiRequest(`/api/admin/advertisers/${id}`, 'PUT', data);
  const jsonData = await response.json();
  
  if (!jsonData?.data?.advertiser) {
    throw new Error(jsonData?.error || 'Failed to update advertiser');
  }
  
  return jsonData.data.advertiser;
}

export async function deleteAdvertiser(id: number): Promise<Advertiser> {
  const response = await apiRequest(`/api/admin/advertisers/${id}`, 'DELETE');
  const jsonData = await response.json();
  
  if (!jsonData?.data?.advertiser) {
    throw new Error(jsonData?.error || 'Failed to delete advertiser');
  }
  
  return jsonData.data.advertiser;
}
